import { useState, useEffect } from "react";
import type { JSONContent } from "../unified";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { CategorySelector, CRITERIA_CATEGORY_COLORS, type CriteriaCategory } from "@/components/CategorySelector";
import { fieldLabelClass, textareaClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useCreateTeacherComment } from "@/api/hooks";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications, ui, placeholders } from "@/strings";
import type { NotificationKind } from "@/providers/NotificationProvider";
import { mapOffsetRangeToSpan, OffsetMappingError } from "@/utils/offsetsToPaths";

const MAX_COMMENT_LENGTH = 1000;
const DEFAULT_CATEGORY: CriteriaCategory = "other";

interface AddCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignmentId: string;
  documentId: string;
  bodyJson: JSONContent | Record<string, unknown>;  // TipTap document structure for path conversion
  selection: {
    startOffset: number;
    endOffset: number;
    phrase: string;
    beforeContext: string | null;
    afterContext: string | null;
    containingPre?: string | null;
    containingSel?: string | null;
    containingPost?: string | null;
  };
  teacherName: string;  // From AuthUser context
}

export function AddCommentModal({
  isOpen,
  onClose,
  assignmentId,
  documentId,
  bodyJson,
  selection,
  teacherName,
}: AddCommentModalProps) {
  const [commentText, setCommentText] = useState("");
  const [category, setCategory] = useState<CriteriaCategory>(DEFAULT_CATEGORY);
  const { show } = useNotification();
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";

  // Derive color from category
  const color = CRITERIA_CATEGORY_COLORS[category];

  const createCommentMutation = useCreateTeacherComment(assignmentId, documentId);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCommentText("");
      setCategory(DEFAULT_CATEGORY);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!commentText.trim()) {
      show(notifications.comments.enterCommentRequired, TOAST_ERROR);
      return;
    }

    if (commentText.length > MAX_COMMENT_LENGTH) {
      show(notifications.comments.maxLengthExceeded(MAX_COMMENT_LENGTH), TOAST_ERROR);
      return;
    }

    try {
      // Convert plaintext offsets to path-based coordinates
      const span = mapOffsetRangeToSpan(
        bodyJson,
        selection.startOffset,
        selection.endOffset
      );

      await createCommentMutation.mutateAsync({
        start_path: span.start_path,
        start_char: span.start_char,
        end_path: span.end_path,
        end_char: span.end_char,
        comment_text: commentText.trim(),
        category,
        phrase: selection.phrase,
        before_context: selection.beforeContext,
        after_context: selection.afterContext,
      });

      show(notifications.comments.added, TOAST_SUCCESS);
      onClose();
    } catch (error) {
      if (error instanceof OffsetMappingError) {
        console.error('Failed to map offsets to paths:', error);
        show(notifications.comments.addFailedWithReason('Invalid selection position.'), TOAST_ERROR);
      } else {
        show(notifications.comments.addFailed, TOAST_ERROR);
      }
    }
  };

  const isOverLimit = commentText.length > MAX_COMMENT_LENGTH;
  const charCount = commentText.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader title={`${ui.actions.add} ${ui.labels.comment}`} onClose={onClose} />
      <ModalBody>
        {/* Preview Section */}
        <div className="rounded-lg border border-border bg-surface-secondary p-4">
          <div className="text-sm text-text-theme-secondary space-y-2">
            {selection.beforeContext && (
              <p className="text-text-theme-muted">{selection.beforeContext}</p>
            )}
            {selection.containingSel ? (
              <p>
                <span>{selection.containingPre}</span>
                <span
                  className="rounded px-1"
                  style={{ backgroundColor: `${color}33`, borderBottom: `2px solid ${color}` }}
                >
                  {selection.containingSel}
                </span>
                <span>{selection.containingPost}</span>
              </p>
            ) : (
              <p>
                <span
                  className="rounded px-1"
                  style={{
                    backgroundColor: `${color}33`,
                    borderBottom: `2px solid ${color}`,
                  }}
                >
                  {selection.phrase}
                </span>
              </p>
            )}
            {selection.afterContext && (
              <p className="text-text-theme-muted">{selection.afterContext}</p>
            )}
          </div>

          {/* Preview Tooltip */}
          {commentText.trim() && (
            <div className="mt-4 rounded-xl border border-border bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <span
                  className="h-2 w-10 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-semibold text-text-theme-secondary">
                    {teacherName} {ui.labels.says}
                  </p>
                  <p className="text-sm text-text-theme-secondary">{commentText}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Category Picker */}
        <CategorySelector selectedCategory={category} onCategoryChange={setCategory} />

        {/* Comment Input */}
        <label className={fieldLabelClass}>
          {ui.labels.comment}
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className={textareaClass}
            rows={4}
            placeholder={placeholders.comments.add}
            autoFocus
          />
          <div className="flex justify-between text-xs mt-1">
            <span className="text-text-theme-muted">
              {ui.labels.visibleToStudent}
            </span>
            <span className={isOverLimit ? "text-semantic-error" : "text-text-theme-muted"}>
              {charCount} {" "}
              {"/"}
              {" "}
              {MAX_COMMENT_LENGTH}
            </span>
          </div>
        </label>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className={buttonVariants.secondary}
          disabled={createCommentMutation.isPending}
        >
          {ui.actions.cancel}
        </button>
        <button
          type="button"
          onClick={() => void handleSubmit()}
          className={buttonVariants.primary}
          disabled={
            !commentText.trim() ||
            isOverLimit ||
            createCommentMutation.isPending
          }
        >
          {createCommentMutation.isPending ? ui.actions.submitting : `${ui.actions.add} ${ui.labels.comment}`}
        </button>
      </ModalFooter>
    </Modal>
  );
}
